import { useState } from "react";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { Tooltip } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import Card from "components/UI/Card/Card.js";
import CardBody from "components/UI/Card/CardBody.js";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import FieldglassButton from "components/UI/FieldglassButton";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";

import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { PaginatedResult } from "services/@types";
import { Sow } from "services/input-core/sows/@types";
import {
  prefetchData,
  useGetSOWsOperative,
} from "services/input-core/sows/get-sows-operative";
import {
  getBadgeFromStatus,
  getFilters,
  getSorting,
  removeElementsFromArray,
} from "utils/functions";
import { useSubscription } from "store/auth/auth-store";
import { useTranslation } from "react-i18next";
import { TitlePages } from "components/UI/TitlePage/TitlePages";
import { useGetSowBlacklist } from "services/input-core/appconfigs/get-blacklisted-entities";
import { FilterSowStatusEnum } from "utils/@types";
import { useSowContext } from "store/sow";

export default function List() {
  const queryClient = useQueryClient();
  const { t } = useTranslation("sow-table");
  const { getSows } = useSowContext();

  const { subscription } = useSubscription();

  const { data: sowAdmBlacklist } = useGetSowBlacklist();

  const columns: ColumnDef<any, any>[] = [
    {
      header: t("status"),
      footer: (props: any) => props.column.id,
      accessorKey: "status",
      cell: (info: any) =>
        getBadgeFromStatus(info.getValue(), (text: string) =>
          t(text, { ns: "status" })
        ),
    },
    {
      footer: (props: any) => props.column.id,
      header: "ID",
      accessorKey: "sowID",
    },
    {
      header: t("name"),
      footer: (props: any) => props.column.id,
      accessorKey: "sowName",
    },
    {
      header: t("supplier"),
      footer: (props: any) => props.column.id,
      accessorKey: "supplierName",
    },
    {
      header: t("start"),
      accessorKey: "startDate",
      cell: (info: any) => info.getValue(),
      sortDescFirst: true,
    },
    {
      header: t("end"),
      accessorKey: "endDate",
      cell: (info: any) => info.getValue(),
      sortDescFirst: true,
    },
    {
      header: t("actions"),
      accessorKey: "actions",
      cell: ({ row }: any) => (
        <div className="actions-right">
          <Tooltip placement="top" title="View">
            <Link to={"/app/sows/profile?id=" + row?.original?.sowID}>
              <ButtonTS
                round
                justIcon
                size="sm"
                color="info"
                key={new Date().getTime()}
              >
                <ArrowForwardIosIcon />
              </ButtonTS>
            </Link>
          </Tooltip>
        </div>
      ),
      enableColumnFilter: false,
    },
  ];

  const [tableData, setTableData] = useState({
    pageSize: 10,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const { data, isLoading, isFetching } = useGetSOWsOperative(
    {
      "page[size]": tableData.pageSize,
      "page[index]": tableData.pageIndex,
      ...getFilters(tableData.columnFilters),
      ...getSorting(tableData.sorting),
    },
    {
      staleTime: 1000 * 60 * 5,
      onSuccess: (data: PaginatedResult<Sow>) => {
        getSows(data.result);
        prefetchData(
          queryClient,
          {
            "page[size]": tableData.pageSize,
            "page[index]": tableData.pageIndex + 1,
            ...getFilters(tableData.columnFilters),
            ...getSorting(tableData.sorting),
          },
          subscription?.subscriptionId
        );
        prefetchData(
          queryClient,
          {
            "page[size]": tableData.pageSize,
            "page[index]":
              tableData.pageIndex > 0 ? tableData.pageIndex - 1 : 0,
            ...getFilters(tableData.columnFilters),
            ...getSorting(tableData.sorting),
          },
          subscription?.subscriptionId
        );
        setTableData((state: any) => ({
          ...state,
          pageSize: data.perPage,
          pageIndex: data.toSkip / data.perPage,
        }));
      },
    }
  );

  return (
    <Card>
      <CardHeader color="rose" icon>
        <CardIcon color="rose">
          <Search />
        </CardIcon>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <TitlePages type="sidebar" title="sows" />
          <FieldglassButton type="sow" />
        </div>
      </CardHeader>
      <CardBody>
        {isLoading || isFetching ? (
          <Skeleton />
        ) : (
          <PaginatedTable
            id="sows-table"
            columns={columns}
            data={
              removeElementsFromArray(
                data?.result,
                sowAdmBlacklist?.sowBlackList ?? []
              ) || []
            }
            tableState={tableData}
            pageCount={data?.totalPages}
            setTableState={setTableData}
            fixedPageSize
            statusToFilter={FilterSowStatusEnum}
          />
        )}
      </CardBody>
    </Card>
  );
}
